import { useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button, cn } from '@blimo/ui';

/**
 * Signature pad on a canvas (pointer events: mouse, pen, touch). After every stroke `onChange` receives the
 * drawing as PNG data URI (`data:image/png;base64,…`), `null` after „Löschen“. The API validates the image.
 * @param {{ onChange: (dataUri: string|null) => void, label?: string, invalid?: boolean, describedBy?: string, className?: string }} props
 */
export function SignaturePad({ onChange, label = 'Unterschrift', invalid = false, describedBy, className }) {
  const canvasRef = useRef(/** @type {HTMLCanvasElement|null} */ (null));
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const context = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    try {
      return canvas.getContext('2d');
    } catch {
      return null;
    }
  };

  // Scale the drawing buffer to the displayed size (sharp lines on high-DPI screens).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 600;
    const height = canvas.clientHeight || 200;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = context();
    if (!ctx) return;
    ctx.scale?.(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  /** @param {import('react').PointerEvent<HTMLCanvasElement>} event */
  const point = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  /** @param {import('react').PointerEvent<HTMLCanvasElement>} event */
  function start(event) {
    event.preventDefault();
    drawing.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const ctx = context();
    const { x, y } = point(event);
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  }

  /** @param {import('react').PointerEvent<HTMLCanvasElement>} event */
  function move(event) {
    if (!drawing.current) return;
    const ctx = context();
    const { x, y } = point(event);
    ctx?.lineTo(x, y);
    ctx?.stroke();
    if (!hasInk) setHasInk(true);
  }

  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    setHasInk(true);
    onChange(exportPng());
  }

  function exportPng() {
    try {
      const uri = canvasRef.current?.toDataURL('image/png');
      return typeof uri === 'string' && uri.startsWith('data:image/png') ? uri : null;
    } catch {
      return null;
    }
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = context();
    if (canvas && ctx) {
      ctx.save?.();
      ctx.setTransform?.(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore?.();
    }
    setHasInk(false);
    onChange(null);
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`${label} – mit Finger, Stift oder Maus im Feld unterschreiben`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={cn('h-48 w-full touch-none rounded-md border bg-white', invalid && 'border-destructive')}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{hasInk ? 'Unterschrift erfasst.' : 'Noch keine Unterschrift.'}</span>
        <Button type="button" variant="outline" size="sm" onClick={clear} disabled={!hasInk}>
          <Eraser aria-hidden="true" /> Löschen
        </Button>
      </div>
    </div>
  );
}
